import React from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import PageHeader from '../components/PageHeader';
import DirectionsList from '../components/DirectionsList';
import NavigationView from '../components/NavigationView';
import { useTripPlan } from '../hooks/useTripPlan';
import { formatDistance, formatTime, formatTemperature, formatSpeed, formatConsumption } from '../lib/locale';

/**
 * DrivePage — the in-drive view. Map-first: a live NavigationView fills the
 * top of the screen (heading-up camera, maneuver banner, SoC + charger
 * overlays, simulation mode). Below it, a scrollable panel keeps the trip
 * detail (destination, ETA, conditions, full step list) that was here before.
 */
export default function DrivePage() {
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale } = useLocale();

  const { loading, error, vehicle, route, socTrace, chargingStops, summary } = useTripPlan(searchParams);

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Loading drive" backTo={`/result/${tripId}?${searchParams.toString()}`} />
        <div className="loading-state">Preparing drive mode…</div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="page">
        <PageHeader title="Drive failed" backTo="/" />
        <div className="error-banner">{error || 'No trip data'}</div>
      </div>
    );
  }

  const nextStop = chargingStops?.stops?.[0];
  const currentWeather = socTrace?.points?.[0] || {};
  const currentSpeed = route?.segments?.[0]?.avg_speed_kmh || 0;
  const consumption = vehicle?.consumption_wh_km || 0;
  const stops = chargingStops?.stops || [];

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PageHeader
        title="Driving"
        backTo={`/result/${tripId}?${searchParams.toString()}`}
        backLabel="End drive"
      />

      {/* ---- MAP-FIRST: live navigation fills the top of the screen ---- */}
      <div style={{
        margin: '0 -16px',
        height: '58vh',
        minHeight: 360,
        position: 'relative',
        background: 'var(--bg-secondary)',
      }}>
        <NavigationView
          route={route}
          stops={stops}
          socTrace={socTrace}
          vehicle={vehicle}
          locale={locale}
        />
      </div>

      {/* ---- Scrollable detail panel below the map ---- */}
      <div style={{ padding: '14px 0 24px' }}>
        {/* Destination + key numbers */}
        <div style={{
          background: 'var(--bg-card)',
          border: '0.5px solid var(--border-subtle)',
          borderRadius: 14,
          padding: 14,
        }}>
          <div style={{
            fontSize: 16,
            fontWeight: 500,
            color: 'var(--text-primary)',
            marginBottom: 4,
            paddingBottom: 8,
            borderBottom: '0.5px solid var(--border-subtle)',
          }}>
            {summary.destination?.name || 'Destination'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, paddingTop: 8 }}>
            <Metric label="Distance" value={formatDistance(summary.distance_km, locale)} />
            <Metric
              label="Arrival SoC"
              value={`${Math.round(summary.final_soc_pct)}% ± ${summary.confidence_pts}`}
              accent
            />
            <Metric label="ETA" value={formatTime(summary.total_time_min)} top />
            {nextStop && (
              <Metric label="Next stop" value={nextStop.name} top warning />
            )}
          </div>
        </div>

        {/* Charge alert */}
        {nextStop && (
          <div
            style={{
              background: 'linear-gradient(90deg, rgba(255, 184, 77, 0.12), rgba(255, 184, 77, 0.02))',
              border: '0.5px solid rgba(255, 184, 77, 0.3)',
              borderLeft: '3px solid var(--accent-warning)',
              borderRadius: 10,
              padding: '10px 12px',
              marginTop: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
            }}
            onClick={() => navigate(`/stop/${nextStop.station_id}?trip=${tripId}`)}
          >
            <span style={{ color: 'var(--accent-warning)', fontSize: 18 }}>⚡</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: 'var(--accent-warning)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>
                Charge in {formatDistance(nextStop.distance_from_origin_km, locale)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Arrive {Math.round(nextStop.arrive_soc_pct)}% · {formatTime(nextStop.charge_time_min, { compact: true })} charge
              </div>
            </div>
          </div>
        )}

        {/* Conditions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          padding: '14px 12px',
          background: 'var(--bg-card)',
          borderRadius: 12,
          border: '0.5px solid var(--border-subtle)',
          marginTop: 12,
        }}>
          <Condition icon="↗" label="Ambient" value={formatTemperature(currentWeather.ambient_c, locale)} />
          <Condition icon="~" label="Wind" value={formatSpeed(currentWeather.wind_speed_kmh, locale)} />
          <Condition icon="⏱" label="Speed" value={formatSpeed(currentSpeed, locale)} />
          <Condition icon="⚡" label="Use" value={formatConsumption(consumption, locale)} />
        </div>

        {/* Full step list — collapsible, for reviewing the whole route */}
        {route?.maneuvers?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <DirectionsList maneuvers={route.maneuvers} locale={locale} defaultExpanded={false} />
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, accent, warning, top }) {
  return (
    <div style={top ? { marginTop: 10 } : undefined}>
      <div style={{
        fontSize: 10,
        color: warning ? 'var(--accent-warning)' : 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 16,
        fontWeight: 500,
        color: accent ? 'var(--accent-primary)' : 'var(--text-primary)',
        marginTop: 2,
      }}>
        {value}
      </div>
    </div>
  );
}

function Condition({ icon, label, value }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 18, color: 'var(--text-tertiary)' }}>{icon}</div>
      <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 4, fontWeight: 500 }}>{value}</div>
      <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
    </div>
  );
}
